/**
 * ============================================================================
 * Pricing 模块 - 常量定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义产品工价管理模块的常量
 * - 包含：选项映射、默认值等
 * 
 * @module types/pricing/constants
 * @generated Manual (AI-assisted)
 * @last-updated 2025-11-30
 * ============================================================================
 */

import {
  PriceMode,
  CalcType,
  CalcRule,
  PriceType,
  WageRecordStatus,
  CommonStatus,
} from './enums'

/**
 * 计价模式选项
 */
export const PRICE_MODE_OPTIONS = [
  { label: '按产品计价', value: PriceMode.PRODUCT },
  { label: '按工序计价', value: PriceMode.OPERATION },
] as const

/**
 * 计算方式选项
 */
export const CALC_TYPE_OPTIONS = [
  { label: '计件', value: CalcType.PIECE },
  { label: '计时', value: CalcType.TIME },
  { label: '混合', value: CalcType.MIXED },
] as const

/**
 * 不良品/返工件计算规则选项
 */
export const CALC_RULE_OPTIONS = [
  { label: '不计入', value: CalcRule.EXCLUDE },
  { label: '半价计算', value: CalcRule.HALF },
  { label: '全价计算', value: CalcRule.FULL },
] as const

/**
 * 计价类型选项
 */
export const PRICE_TYPE_OPTIONS = [
  { label: '计件(元/件)', value: PriceType.PIECE },
  { label: '计时(元/小时)', value: PriceType.TIME },
] as const

/**
 * 工资记录状态选项
 */
export const WAGE_STATUS_OPTIONS = [
  { label: '待审核', value: WageRecordStatus.PENDING },
  { label: '已确认', value: WageRecordStatus.CONFIRMED },
  { label: '已结算', value: WageRecordStatus.SETTLED },
  { label: '已作废', value: WageRecordStatus.VOIDED },
] as const

/**
 * 通用状态选项
 */
export const COMMON_STATUS_OPTIONS = [
  { label: '停用', value: CommonStatus.INACTIVE },
  { label: '启用', value: CommonStatus.ACTIVE },
] as const

/**
 * 计价模式映射
 */
export const PRICE_MODE_MAP: Record<PriceMode, string> = {
  [PriceMode.PRODUCT]: '按产品计价',
  [PriceMode.OPERATION]: '按工序计价',
}

/**
 * 计算方式映射
 */
export const CALC_TYPE_MAP: Record<CalcType, string> = {
  [CalcType.PIECE]: '计件',
  [CalcType.TIME]: '计时',
  [CalcType.MIXED]: '混合',
}

/**
 * 计算规则映射
 */
export const CALC_RULE_MAP: Record<CalcRule, string> = {
  [CalcRule.EXCLUDE]: '不计入',
  [CalcRule.HALF]: '半价计算',
  [CalcRule.FULL]: '全价计算',
}

/**
 * 计价类型映射
 */
export const PRICE_TYPE_MAP: Record<PriceType, string> = {
  [PriceType.PIECE]: '计件(元/件)',
  [PriceType.TIME]: '计时(元/小时)',
}

/**
 * 工资状态映射
 */
export const WAGE_STATUS_MAP: Record<WageRecordStatus, string> = {
  [WageRecordStatus.PENDING]: '待审核',
  [WageRecordStatus.CONFIRMED]: '已确认',
  [WageRecordStatus.SETTLED]: '已结算',
  [WageRecordStatus.VOIDED]: '已作废',
}

/**
 * 工资状态颜色映射
 */
export const WAGE_STATUS_COLOR_MAP: Record<WageRecordStatus, string> = {
  [WageRecordStatus.PENDING]: 'warning',
  [WageRecordStatus.CONFIRMED]: 'processing',
  [WageRecordStatus.SETTLED]: 'success',
  [WageRecordStatus.VOIDED]: 'error',
}

/**
 * 默认配置值
 */
export const DEFAULT_CONFIG = {
  /** 默认加班系数 */
  OVERTIME_FACTOR: 1.5,
  /** 默认夜班系数 */
  NIGHT_SHIFT_FACTOR: 1.0,
  /** 默认节假日系数 */
  HOLIDAY_FACTOR: 1.0,
  /** 默认熟练度系数 */
  SKILL_FACTOR: 1.0,
  /** 默认币种 */
  CURRENCY: 'CNY',
} as const
